import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchRoutes, fetchDestinations, fetchPhotos, photoUrl } from "@/lib/api";
import { RouteCard } from "@/components/RouteCard";
import { WawTracker } from "@/components/WawTracker";
import { RotatingHero } from "@/components/RotatingHero";
import { ArrowRight, Compass, Mountain, Waves, Landmark } from "lucide-react";

const HERO_IMG =
    "https://images.unsplash.com/photo-1780926539659-2ecaa95b2f62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb3RvcmN5Y2xlJTIwY29hc3RhbCUyMHJvYWR8ZW58MHx8fHwxNzgzMDI0NDA3fDA&ixlib=rb-4.1.0&q=85";

const WAW_IMG =
    "https://images.unsplash.com/photo-1566123561340-af250b1ec246?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxkcmFtYXRpYyUyMGRhcmslMjBpcmlzaCUyMGxhbmRzY2FwZSUyMGNsaWZmc3xlbnwwfHx8fDE3ODMwMjQ0MDd8MA&ixlib=rb-4.1.0&q=85";

const CATEGORIES = [
    { name: "Coastal", icon: Waves, desc: "Cliff-edge tarmac, salt spray, endless Atlantic." },
    { name: "Mountain", icon: Mountain, desc: "Hairpin climbs, bog-top plateaus, thin air." },
    { name: "Peninsula", icon: Compass, desc: "Fingers of land reaching into the ocean." },
    { name: "Historic", icon: Landmark, desc: "Roads that carry a thousand years of story." },
];

export default function Home() {
    const [routes, setRoutes] = useState([]);
    const [destinations, setDestinations] = useState([]);
    const [heroImages, setHeroImages] = useState([]);

    useEffect(() => {
        fetchRoutes().then(setRoutes).catch(() => {});
        fetchDestinations().then(setDestinations).catch(() => {});
        fetchPhotos()
            .then((photos) => {
                const urls = photos.map((p) => photoUrl(p.url));
                // Shuffle for variety
                for (let i = urls.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [urls[i], urls[j]] = [urls[j], urls[i]];
                }
                setHeroImages(urls);
            })
            .catch(() => {});
        document.title = "MotorcycleRoutesIreland — Ireland's Greatest Motorcycle Roads";
    }, []);

    const featured = routes.slice(0, 4);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-screen flex items-end overflow-hidden">
                <div className="absolute inset-0 grain">
                    <RotatingHero images={heroImages} fallback={HERO_IMG} className="absolute inset-0" intervalMs={9000} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#14161A] via-[#14161A]/60 to-[#14161A]/30" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#14161A]/70 via-transparent to-transparent" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-24 pt-32 w-full on-hero">
                    <div className="max-w-3xl">
                        <div className="eyebrow fade-up">Est. Ireland · The Field Guide</div>
                        <h1 className="mt-6 font-display font-black uppercase text-[var(--fg)] leading-[0.85] tracking-tighter text-6xl md:text-8xl lg:text-9xl fade-up fade-up-delay-1">
                            Ride the<br /><span className="text-[#E87722]">edge</span> of Europe.
                        </h1>
                        <p className="mt-8 font-body text-lg md:text-xl text-[var(--fg-muted)] max-w-2xl leading-relaxed fade-up fade-up-delay-2">
                            A curated field guide to the greatest motorcycle roads on the island of Ireland — from the full 2,500 km Wild Atlantic Way down to a twelve-kilometre pass through the Caha Mountains you'll remember for the rest of your riding life.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4 fade-up fade-up-delay-3">
                            <Link
                                to="/routes"
                                data-testid="hero-cta-routes"
                                className="group inline-flex items-center gap-3 bg-[#E87722] hover:bg-[#F28C38] text-black px-8 py-4 font-display font-bold uppercase tracking-[0.2em] text-sm transition-all"
                            >
                                Explore Routes <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link
                                to="/destinations"
                                data-testid="hero-cta-destinations"
                                className="inline-flex items-center gap-3 border border-white/20 hover:border-[#E87722] hover:text-[#E87722] text-[var(--fg)] px-8 py-4 font-display font-bold uppercase tracking-[0.2em] text-sm transition-all"
                            >
                                Destination Guides
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Bottom stats bar */}
                <div className="absolute bottom-0 left-0 right-0 border-t border-[var(--border-hair)] bg-[var(--surface)]/70 backdrop-blur-md">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 grid grid-cols-2 md:grid-cols-4 gap-6">
                        {[
                            { n: "2,500", u: "km of Wild Atlantic Way" },
                            { n: "32", u: "counties covered" },
                            { n: "9", u: "peninsulas" },
                            { n: "∞", u: "reasons to come back" },
                        ].map((s, i) => (
                            <div key={i} className="flex items-baseline gap-3">
                                <div className="font-display font-black text-3xl md:text-4xl text-[var(--fg)] leading-none">{s.n}</div>
                                <div className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-[var(--fg-muted)] font-body">{s.u}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED ROUTES */}
            <section className="py-24 lg:py-32 relative">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div>
                            <div className="eyebrow">Featured</div>
                            <h2 className="mt-3 font-display font-black uppercase text-4xl md:text-6xl leading-none tracking-tight text-[var(--fg)]">
                                Roads worth<br />crossing an ocean for.
                            </h2>
                        </div>
                        <Link
                            to="/routes"
                            data-testid="featured-view-all"
                            className="group inline-flex items-center gap-2 font-display font-bold uppercase tracking-[0.2em] text-sm text-[#E87722] hover:gap-4 transition-all"
                        >
                            View all routes <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {featured.map((r, i) => (
                            <RouteCard key={r.slug} route={r} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* WILD ATLANTIC WAY SPOTLIGHT */}
            <section className="relative min-h-[80vh] flex items-center overflow-hidden border-y border-[var(--border-hair)]">
                <div className="absolute inset-0">
                    <img src={WAW_IMG} alt="Wild Atlantic Way" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#14161A] via-[#14161A]/80 to-[#14161A]/30" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full">
                    <div className="relative max-w-2xl on-hero">
                        <div className="eyebrow">The Signature Ride</div>
                        <h2 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
                            The Wild<br />Atlantic Way.
                        </h2>
                        <p className="mt-8 font-body text-[var(--fg-muted)] text-lg leading-relaxed">
                            2,500 kilometres. Nine peninsulas. From Kinsale in the sunny south-east to Malin Head where the tarmac finally admits defeat to the North Atlantic. It's the longest defined coastal touring route on earth and the reason most riders come to Ireland in the first place.
                        </p>
                        <div className="mt-10 grid grid-cols-3 gap-6 py-8 border-y border-[var(--border-hair)] max-w-lg">
                            <div>
                                <div className="font-display font-black text-4xl text-[#E87722] leading-none">2,500</div>
                                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[var(--fg-muted)]">Kilometres</div>
                            </div>
                            <div>
                                <div className="font-display font-black text-4xl text-[var(--fg)] leading-none">14</div>
                                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[var(--fg-muted)]">Days minimum</div>
                            </div>
                            <div>
                                <div className="font-display font-black text-4xl text-[var(--fg)] leading-none">9</div>
                                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[var(--fg-muted)]">Peninsulas</div>
                            </div>
                        </div>
                        <Link
                            to="/ride-the-whole-thing"
                            data-testid="waw-cta"
                            className="mt-8 inline-flex items-center gap-3 bg-[#E87722] hover:bg-[#F28C38] text-black px-8 py-4 font-display font-bold uppercase tracking-[0.2em] text-sm transition-all"
                        >
                            Ride the whole thing <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* WAW PROGRESS TRACKER */}
            <WawTracker />

            {/* CATEGORIES */}
            <section className="py-24 lg:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="mb-16">
                        <div className="eyebrow">By Character</div>
                        <h2 className="mt-3 font-display font-black uppercase text-4xl md:text-6xl leading-none tracking-tight text-[var(--fg)] max-w-3xl">
                            Every kind of road<br />this island has to offer.
                        </h2>
                    </div>                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {CATEGORIES.map((c, i) => (
                            <Link
                                key={c.name}
                                to={`/routes?category=${c.name}`}
                                data-testid={`category-${c.name.toLowerCase()}`}
                                className="group relative p-8 border border-[var(--border-hair)] hover:border-[#E87722] bg-[var(--card-bg)] transition-all hover:-translate-y-1 fade-up"
                                style={{ animationDelay: `${i * 80}ms` }}
                            >
                                <c.icon size={32} className="text-[#E87722]" strokeWidth={1.5} />
                                <h3 className="mt-6 font-display font-black uppercase text-3xl text-[var(--fg)] group-hover:text-[#E87722] transition-colors">
                                    {c.name}
                                </h3>
                                <p className="mt-3 text-[var(--fg-muted)] font-body text-sm leading-relaxed">
                                    {c.desc}
                                </p>
                                <ArrowRight size={18} className="mt-6 text-[var(--fg-dim)] group-hover:text-[#E87722] group-hover:translate-x-1 transition-all" />
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* DESTINATIONS TEASER */}
            <section className="py-24 lg:py-32 border-t border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div>
                            <div className="eyebrow">Regions</div>
                            <h2 className="mt-3 font-display font-black uppercase text-4xl md:text-6xl leading-none tracking-tight text-[var(--fg)]">
                                Where to ride,<br />where to sleep.
                            </h2>
                        </div>
                        <Link
                            to="/destinations"
                            data-testid="destinations-view-all"
                            className="group inline-flex items-center gap-2 font-display font-bold uppercase tracking-[0.2em] text-sm text-[#E87722] hover:gap-4 transition-all"
                        >
                            All destinations <ArrowRight size={16} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {destinations.slice(0, 6).map((d, i) => (
                            <Link
                                key={d.slug}
                                to={`/destinations/${d.slug}`}
                                data-testid={`destination-${d.slug}`}
                                className="group relative aspect-[4/5] overflow-hidden border border-[var(--border-hair)] hover:border-[#E87722] fade-up"
                                style={{ animationDelay: `${i * 80}ms` }}
                            >
                                <img
                                    src={d.hero_image}
                                    alt={d.name}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                                <div className="relative h-full flex flex-col justify-end p-8">
                                    <div className="text-xs font-body uppercase tracking-[0.28em] text-[#E87722] mb-2">{d.tagline}</div>
                                    <h3 className="font-display font-black uppercase text-5xl text-[var(--fg)] leading-none tracking-tight">
                                        {d.name}
                                    </h3>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-32 border-t border-[var(--border-hair)] relative overflow-hidden">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                    <div className="eyebrow">Ready?</div>
                    <h2 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter">
                        The map is<br />waiting.
                    </h2>
                    <p className="mt-8 font-body text-[var(--fg-muted)] text-lg leading-relaxed max-w-2xl mx-auto">
                        Pick a route. Download the GPX. Fuel up. The rest is between you and the road.
                    </p>
                    <Link
                        to="/routes"
                        data-testid="final-cta"
                        className="mt-10 inline-flex items-center gap-3 bg-[#E87722] hover:bg-[#F28C38] text-black px-10 py-5 font-display font-bold uppercase tracking-[0.2em] transition-all"
                    >
                        Start Riding <ArrowRight size={18} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
